import { computed, unref, watch } from "vue";
import { useOrganizationNameCacheStore } from "@/entities/organization/model/organizationNameCache.store";

/**
 * Реактивно резолвит имя организации по id.
 * Lazy: запрос летит только при первом обращении (вотчер), результат кешируется.
 *
 * @param {string|number|import('vue').Ref<string|number>} idRef
 * @param {string|import('vue').Ref<string>} [fallbackNameRef] Сразу известное имя
 *   (например, прилетевшее с user-payload). Если задано — запрос не делается.
 */
export function useOrganizationName(idRef, fallbackNameRef) {
  const cache = useOrganizationNameCacheStore();

  const id = computed(() => {
    const v = typeof idRef === "function" ? idRef() : unref(idRef);
    return v == null || v === "" ? null : v;
  });
  const fallbackName = computed(() => {
    const v =
      typeof fallbackNameRef === "function"
        ? fallbackNameRef()
        : unref(fallbackNameRef);
    return v || null;
  });

  const entry = computed(() => {
    void cache.entriesRef.value;
    return id.value != null ? cache.get(id.value) : null;
  });

  const name = computed(() => fallbackName.value ?? entry.value?.data ?? null);
  const isLoading = computed(
    () => entry.value?.status === "loading" && !entry.value?.data
  );

  watch(
    [id, fallbackName],
    ([nextId, nextFallback]) => {
      if (nextId == null || nextFallback) return;
      cache.fetchName(nextId);
    },
    { immediate: true }
  );

  return { name, isLoading };
}
